import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { QuestionType as QuestionDataType } from "types/dataVoteType";
import { QuestionType } from "types/questionTypes";
import { HeadingLevel } from "types/headingLevel";
import { convertLineBreak } from "services/convertLineBreak";
import { frameTransparentBackground } from "components/common/backgroundFrame";
import { getHeadingTag } from "components/common/heading";
import { getQuestionTitleId } from "../VoteGroup";
import {
    VOTEBOX_FONT_SIZE_BODY,
    VOTEBOX_FONT_SIZE_CAPTION,
    VOTEBOX_FONT_WEIGHT_BOLD,
    VOTEBOX_LINE_HEIGHT,
    VOTEBOX_MUTED_BACKGROUND_COLOR,
    VOTEBOX_OPTION_RADIUS,
    VOTEBOX_SECONDARY_TEXT_COLOR,
} from "../voteboxTokens";

interface Props {
    headingLevel?: HeadingLevel;
    question: QuestionDataType;
    voteTitle?: string; // In case of grouped deliberation
}

const VoteHeader = ({ headingLevel = 1, question, voteTitle }: Props) => {
    const { t } = useTranslation();

    const displayTitle = voteTitle || question.title;
    const isElection = question.type === QuestionType.ELECTION;
    const isUninominalElection = isElection && 1 === question.numberOfSeats;

    function getNumberOfAnswersToSelectLabel() {
        if (question.minNumberOfAnswers === question.maxNumberOfAnswers) {
            return t("youMustSelectMinimumOfAnswers", {
                min: question.maxNumberOfAnswers,
            });
        }

        return t("youMustMustSelectBetweenMinAndMaxAnswers", {
            min: question.minNumberOfAnswers,
            max: question.maxNumberOfAnswers,
        });
    }

    function getVoteTypeLabel() {
        const secrecyLabel = question.isSecret
            ? t("secretVote")
            : t("publicVote");
        const electionLabel = isUninominalElection
            ? t("uninominalElection")
            : t("plurinominalElection");
        // Under a voteTitle (grouped deliberation), the secrecy is not shown.
        const labels = [
            voteTitle ? null : secrecyLabel,
            isElection ? electionLabel : null,
        ];

        return labels.filter(Boolean).join(" · ");
    }

    const voteTypeLabel = getVoteTypeLabel();
    // Vote secrecy and election type on one line: "Secret vote · Uninominal election".
    const voteTypeLine = voteTypeLabel ? (
        <VoteType>{voteTypeLabel}</VoteType>
    ) : null;
    const answersToSelectLine =
        isElection && !isUninominalElection ? (
            <VoteType>{getNumberOfAnswersToSelectLabel()}</VoteType>
        ) : null;
    // Under a voteTitle (grouped deliberation), the description is not shown.
    const description =
        !voteTitle && question.description ? (
            <DescriptionWrapper>
                {convertLineBreak(question.description)}
            </DescriptionWrapper>
        ) : null;

    return (
        <Wrapper>
            <Title
                as={getHeadingTag(headingLevel)}
                id={getQuestionTitleId(question.id)}
            >
                {convertLineBreak(displayTitle)}
            </Title>
            {voteTypeLine}
            {answersToSelectLine}
            {description}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    background-color: ${VOTEBOX_MUTED_BACKGROUND_COLOR};
    font-size: ${VOTEBOX_FONT_SIZE_BODY};
    line-height: ${VOTEBOX_LINE_HEIGHT};
    padding: 14px 16px;
    margin-bottom: 1rem;
    margin-left: -0.75rem;
    margin-right: -0.75rem;
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    ${frameTransparentBackground}
`;

// A heading for the structure only (RGAA 9.1): it keeps the look of the former div.
const Title = styled.h1`
    margin: 0;
    font-size: inherit;
    font-weight: inherit;
`;

const DescriptionWrapper = styled.div`
    font-size: ${VOTEBOX_FONT_SIZE_CAPTION};
    line-height: 1.2em;
    max-height: calc(1.2em * 3);
    overflow-y: auto;
`;

const VoteType = styled.div`
    font-size: ${VOTEBOX_FONT_SIZE_CAPTION};
    font-weight: ${VOTEBOX_FONT_WEIGHT_BOLD};
    color: ${VOTEBOX_SECONDARY_TEXT_COLOR};
`;

export default VoteHeader;
